import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import ChevronDownIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { useDispatch } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';

interface SidebarDropdownLinksProps {
  otherClasses?: string;
  title: string;
  link?: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  links?: {
    title: string;
    link: string;
    visible?: boolean;
  }[];
  visible?: boolean;
  setToggle: React.Dispatch<React.SetStateAction<string>>;
  toggle: string;
  disabled?: boolean;
  showChevron?: boolean;
}

const SidebarDropdownLinks: React.FC<SidebarDropdownLinksProps> = ({
  otherClasses,
  links,
  title,
  link,
  setToggle,
  icon,
  activeIcon,
  toggle,
  visible,
  disabled,
  showChevron,
}) => {
  const sidebarDropdownLinksClasses = clsx(
    otherClasses,
    visible ? 'block' : 'hidden',
  );
  const location = useLocation();
  const isExpanded = toggle === title;
  const dropdownId = `dropdown-${title.toLowerCase().replace(/\s+/g, '-')}`;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setToggle(title);
    }
  };

  const dispatch = useDispatch();


  return (
    <div
      className={sidebarDropdownLinksClasses}
      data-testid="sidebar-dropdown-links"
      role="navigation"
      aria-label={`${title} navigation`}
    >
      {link ? (
        <Link
          onClick={() => {
            if (!disabled) {
              setToggle(title);
              dispatch(toggleSwitch());
            }
          }}
          className={clsx(
            'font-inter-variable global-transition flex w-full cursor-pointer items-center gap-2.5 rounded px-2.5 py-2 text-left text-xl leading-6',
            isExpanded
              ? 'text-brand-papery shadow-pricing-plans-card bg-dark-neutral-tones-text/15 font-bold'
              : 'text-neutral-tones-text/50 hover:text-neutral-tones-text font-medium',
            disabled &&
            'hover:!text-neutral-tones-text/50 !text-neutral-tones-text/50 !cursor-not-allowed opacity-50',
          )}
          to={disabled ? '#' : link}
          aria-current={location.pathname === link ? 'page' : undefined}
        >
          {isExpanded ? activeIcon : icon} {title}

          {
            showChevron && (
              <ChevronDownIcon
                className={clsx(
                  'svg-path-current-color-fill transition-transform ml-auto',
                  isExpanded ? 'rotate-180' : '',
                )}
                aria-hidden="true"
              />
            )
          }
        </Link>
      ) : (
        <button
          disabled={disabled}
          onClick={() => {
            setToggle(title);
          }}
          onKeyDown={handleKeyDown}
          className={clsx(
            'global-transition group disabled:hover:[&>p]:text-neutral-tones-text/50 disabled:hover:text-neutral-tones-text/50 flex w-full cursor-pointer items-center justify-between rounded px-2.5 py-2 text-left leading-6 disabled:cursor-not-allowed disabled:opacity-50',
            isExpanded
              ? 'shadow-pricing-plans-card bg-dark-neutral-tones-text/15 text-brand-papery'
              : 'text-neutral-tones-text/50 hover:text-neutral-tones-text',
          )}
          aria-expanded={isExpanded}
          aria-controls={dropdownId}
          aria-label={`${title} menu`}
        >
          <p
            className={clsx(
              'font-inter-variable global-transition flex items-center gap-2.5 text-xl leading-22',
              isExpanded
                ? 'text-brand-papery font-bold'
                : 'text-neutral-tones-text/50 group-hover:text-neutral-tones-text font-medium',
            )}
          >
            {isExpanded ? activeIcon : icon} {title}
          </p>
          <ChevronDownIcon
            className={clsx(
              'svg-path-current-color-fill transition-transform',
              isExpanded ? 'rotate-180' : '',
            )}
            aria-hidden="true"
          />
        </button>
      )}
      {links && (
        <div
          id={dropdownId}
          className={clsx(
            'global-transition flex flex-col gap-15',
            isExpanded
              ? 'mt-15 max-h-80 overflow-auto'
              : 'max-h-0 overflow-hidden',
          )}
          role="menu"
          aria-label={`${title} submenu`}
        >
          {links?.map(
            (node, index) =>
              node.visible && (
                <Link
                  onClick={() => {
                    if (!disabled) {
                      dispatch(toggleSwitch());
                    }
                  }}
                  tabIndex={isExpanded ? 0 : -1}
                  to={node?.link}
                  key={index}
                  className={clsx(
                    'font-inter-variable global-transition py-0.5 pr-2.5 pl-25 text-lg',
                    location.pathname === node.link ||
                      location.pathname.startsWith(node.link + '/')
                      ? 'text-brand-papery font-bold'
                      : 'text-neutral-tones-text/50 hover:text-neutral-tones-text font-medium',
                  )}
                  role="menuitem"
                  aria-current={
                    location.pathname === node.link ? 'page' : undefined
                  }
                >
                  {node?.title}
                </Link>
              ),
          )}
        </div>
      )}
    </div>
  );
};

export default SidebarDropdownLinks;
